import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import moduleRealtimeService from '../../../services/moduleRealtimeService';

const CashFlowSummary = ({ cashflow, onCashflowUpdate }) => {
  const { projectId } = useParams();

  // Real-time subscription for cashflow changes
  React.useEffect(() => {
    if (!projectId) return;

    const cashflowChannel = moduleRealtimeService?.subscribeToCashflowChanges(
      projectId,
      (payload) => {
        console.log('Cashflow change detected:', payload?.event);
        
        // Trigger cashflow refresh when data changes
        if (onCashflowUpdate) {
          onCashflowUpdate();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      moduleRealtimeService?.unsubscribe(cashflowChannel);
    };
  }, [projectId, onCashflowUpdate]);

  const chartData = useMemo(() => {
    if (!cashflow || cashflow?.length === 0) return [];

    let cumulativeInflow = 0;
    let cumulativeOutflow = 0;

    return cashflow?.map((cf) => {
      cumulativeInflow += cf?.inflow || 0;
      cumulativeOutflow += cf?.outflow || 0;

      const date = new Date(cf?.month);
      const monthLabel = date?.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

      return {
        month: monthLabel,
        inflow: cf?.inflow || 0,
        outflow: cf?.outflow || 0,
        cumulativeInflow,
        cumulativeOutflow
      };
    });
  }, [cashflow]);

  if (!cashflow || cashflow?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Cash Flow Summary</h2>
        <p className="text-muted-foreground text-sm">No cash flow data available</p>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact',
      compactDisplay: 'short'
    })?.format(value);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{payload?.[0]?.payload?.month}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry?.color }}>
              {entry?.name}: {formatCurrency(entry?.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h2 className="text-base font-semibold text-foreground">Cash Flow Summary</h2>
        <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
      </div>

      <div className="w-full h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="month"
              tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
              stroke="var(--color-border)"
            />
            <YAxis
              tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
              stroke="var(--color-border)"
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
              iconType="line"
            />
            <Bar
              dataKey="inflow"
              name="Inflows"
              fill="var(--color-success)"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="outflow"
              name="Outflows"
              fill="var(--color-error)"
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="cumulativeInflow"
              name="Cumulative Inflows"
              stroke="var(--color-success)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-success)', r: 3 }}
            />
            <Line
              type="monotone"
              dataKey="cumulativeOutflow"
              name="Cumulative Outflows"
              stroke="var(--color-error)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-error)', r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CashFlowSummary;